import { z } from "zod";

/**
 * Types for the insurance proposal flow, ported from the `jobnimbus-mcp` Worker.
 * Everything here is in whole cents (`…Cents`). The AI reads the carrier estimate in dollars;
 * `rawEstimateSchema` validates that and `extractionFromRaw()` converts it.
 */

/** Trades the estimate reader sorts lines into (Worker `SCHEMA.items.trade`). */
export const TRADES = ["Roofing", "Gutters & Guards", "Siding", "Windows", "Doors", "Fascia & Trim", "Fence", "Interior", "Contents", "General"] as const;

/** What a coverage's printed line RCVs already contain. */
export const LINES_INCLUDE = ["nothing", "tax", "op", "tax_and_op"] as const;
export type LinesInclude = (typeof LINES_INCLUDE)[number];

// ---------- AI output (dollars), as the Worker's record_estimate tool returns it

const num = z.number().finite();
const optNum = num.optional();

const rawRpsSchema = z.looseObject({
  lineItemTotal: optNum,
  lineItemRpsTotal: optNum,
  tax: optNum,
  overhead: optNum,
  profit: optNum,
  rcv: optNum,
  customerPortion: optNum,
  rpsValue: optNum,
});

const rawCoverageSchema = z.looseObject({
  code: z.string(),
  name: z.string(),
  lineItemTotal: num,
  tax: num,
  overhead: num,
  profit: num,
  rcv: num,
  acv: num,
  deductible: num,
  recoverableDepreciation: num,
  nonRecoverableDepreciation: num,
  paidWhenIncurred: z.boolean().optional(),
  linesInclude: z.enum(LINES_INCLUDE).optional(),
  priorPayments: optNum,
  rps: rawRpsSchema.optional(),
});

const rawItemSchema = z.looseObject({
  n: z.number().int(),
  description: z.string().nullish(),
  area: z.string().nullish(),
  coverage: z.string().nullish(),
  /** Free text: the Worker defaults an empty trade to "General" and otherwise keeps what the AI sent. */
  trade: z.string().nullish(),
  building: z.string().nullish(),
  rcv: num,
  depreciation: num,
  recoverable: z.boolean(),
  acv: num,
  rpsFactor: z.string().optional(),
  rpsTotal: optNum,
  paidWhenIncurredAmount: optNum,
});

/** The AI's reading of a carrier estimate, in dollars (input of the Worker's `record_estimate` tool). */
export const rawEstimateSchema = z.looseObject({
  claim: z
    .looseObject({
      carrier: z.string().optional(),
      claimNumber: z.string().optional(),
      policyNumber: z.string().optional(),
      dateOfLoss: z.string().optional(),
      insured: z.string().optional(),
      propertyAddress: z.string().optional(),
    })
    .default({}),
  coverages: z.array(rawCoverageSchema).default([]),
  items: z.array(rawItemSchema).default([]),
  lineItemTotals: z.looseObject({ rcv: optNum, depreciation: optNum, acv: optNum }).optional(),
  groupSummaries: z.record(z.string(), z.string()).optional(),
});
export type RawEstimate = z.infer<typeof rawEstimateSchema>;

// ---------- Estimate extraction (cents)

export interface EstimateClaim {
  carrier?: string;
  claimNumber?: string;
  policyNumber?: string;
  dateOfLoss?: string;
  insured?: string;
  propertyAddress?: string;
}

/** "Summary for Roof Surface Payment Schedule (RPS)" block of a coverage. A field the AI didn't read is undefined. */
export interface RpsSummary {
  lineItemTotalCents?: number;
  /** "RPS Total" column of the RPS Line Item Total row. */
  lineItemRpsTotalCents?: number;
  taxCents?: number;
  overheadCents?: number;
  profitCents?: number;
  rcvCents?: number;
  /** Customer Portion for RPS: never paid by the insurer, and not depreciation. */
  customerPortionCents?: number;
  rpsValueCents?: number;
}

/** One coverage summary block (Dwelling, Other Structures, Ordinance or Law, Contents, …). */
export interface Coverage {
  code: string;
  name: string;
  lineItemTotalCents: number;
  taxCents: number;
  overheadCents: number;
  profitCents: number;
  rcvCents: number;
  acvCents: number;
  deductibleCents: number;
  recoverableDepreciationCents: number;
  nonRecoverableDepreciationCents: number;
  /** Ordinance or Law / code upgrade: the carrier pays it only when incurred. */
  paidWhenIncurred?: boolean;
  linesInclude?: LinesInclude;
  priorPaymentsCents?: number;
  rps?: RpsSummary;
}

/** One numbered line of the carrier estimate. */
export interface EstimateItem {
  /** Line number as printed (revisions can skip numbers). */
  n: number;
  description: string;
  /** Section heading the line is printed under ("Dwelling Roof"). */
  area: string;
  /** Code of the coverage the line belongs to. */
  coverage: string;
  /** One of `TRADES` in practice; "General" when empty. */
  trade: string;
  /** "Main House", "Shed", "Garage", "Fence"… ("Main House" when empty). */
  building: string;
  rcvCents: number;
  depreciationCents: number;
  /** true when depreciation is printed in ( ), false for < > (non-recoverable). */
  recoverable: boolean;
  acvCents: number;
  /** RPS factor as printed ("61%", "N/A"); only on lines in the RPS factor detail. */
  rpsFactor?: string;
  /** The line's RPS Total; only on RPS lines. */
  rpsTotalCents?: number;
  /** Code-upgrade line whose dollars are not in its printed RCV (State Farm "PWARR"). */
  paidWhenIncurredAmountCents?: number;
  /** Set by `normalizeExtraction`: a PWARR line whose amount is outside the estimate's totals. */
  outsideTotals?: boolean;
}

/** A carrier estimate as read by the AI, in cents. */
export interface EstimateExtraction {
  claim: EstimateClaim;
  coverages: Coverage[];
  items: EstimateItem[];
  /** The estimate's overall "Line Item Totals" row, if printed. */
  lineItemTotals?: { rcvCents?: number; depreciationCents?: number; acvCents?: number };
  /** "<trade> - <building>" → one homeowner-friendly sentence. */
  groupSummaries?: Record<string, string>;
}

/** One totals check against the estimate's own printed figures. */
export interface ReconcileCheck {
  name: string;
  ok: boolean;
  detail: string;
}

// ---------- Proposal (what the PDF and the payment schedule are built from)

/** A line on the proposal (an estimate line, or a tax / O&P allocation line). */
export interface ProposalLine {
  description: string;
  rcvCents: number;
  acvCents: number;
  /** Default: the group's `recoverable`, else true. */
  recoverable?: boolean;
  /** Ordinance or Law: paid by the insurer only when incurred. */
  paidWhenIncurred?: boolean;
}

export interface ScopeGroup {
  heading: string;
  summary?: string;
  specs?: string[];
  /** false = this group's depreciation is non-recoverable (e.g. roof-ACV policy). Default true. */
  recoverable?: boolean;
  items: ProposalLine[];
}

export interface ProposalCustomer {
  name: string;
  address: string;
  cityStateZip?: string;
  phone?: string;
  email?: string;
  firstName?: string;
}

export interface ProposalRep {
  name: string;
  phone?: string;
  email?: string;
}

export interface ProposalUpgradeItem {
  description: string;
  /** Optional per-item price, printed only; the contract uses `ProposalUpgrades.totalCents`. */
  priceCents?: number;
}

/** Work beyond the insurance estimate, paid by the customer. */
export interface ProposalUpgrades {
  items?: ProposalUpgradeItem[];
  noCharge?: string[];
  totalCents?: number;
  /** Replaces the default "when due" wording on the payment schedule. */
  due?: string;
}

/** The Worker's `InsuranceProposalInput` (the body of POST /generate-insurance-proposal), in cents. */
export interface ProposalInput {
  customer: ProposalCustomer;
  /** "October 13, 2026". Default: today in America/Chicago. */
  date?: string;
  /** Default 15. */
  validDays?: number;
  claim?: { carrier?: string; claimNumber?: string; policyNumber?: string; dateOfLoss?: string };
  deductibleCents: number;
  system?: { name: string; description?: string };
  intro?: string;
  /** Lines in NMT's contract, grouped. */
  scope: ScopeGroup[];
  /** Approved lines NOT in NMT's contract (shown under "Not included"). */
  excluded?: ProposalLine[];
  /** Default for excluded lines without `recoverable`. Default true. */
  excludedRecoverable?: boolean;
  /** Total recoverable depreciation from the estimate's coverage summaries. */
  claimRecoverableDepCents?: number;
  excludedNote?: string;
  additionalTerms?: string[];
  paymentNotes?: string[];
  scopeNotes?: string[];
  claimNotes?: string[];
  productSelections?: string[];
  preparedBy?: ProposalRep;
  upgrades?: ProposalUpgrades;
  includeGeneralConditions?: boolean;
  includeDeductibleNotice?: boolean;
}

/** Everything the Worker's `computePlan()` returns, in cents. */
export interface ProposalPlan {
  /** RCV of the lines in NMT's contract (including code-upgrade lines). */
  contractRcvCents: number;
  contractAcvCents: number;
  contractDepCents: number;
  /** Recoverable depreciation on contract lines (code-upgrade lines excluded). */
  contractRecDepCents: number;
  /** Non-recoverable depreciation on contract lines (code-upgrade lines excluded); the customer pays it. */
  contractNonRecDepCents: number;
  /** The full deductible entered. */
  deductibleCents: number;
  /** Deductible due to NMT: the deductible, capped at the contract's ACV due. */
  deductibleDueCents: number;
  /** ACV for NMT's work less the deductible due. */
  acvCheckDueCents: number;
  /** Contract ACV excluding code-upgrade lines. */
  contractAcvDueCents: number;
  /** RCV of code-upgrade (paid when incurred) contract lines. */
  contractPwiCents: number;
  /** ACV of code-upgrade lines on the whole claim (contract + excluded). */
  claimPwiAcvCents: number;
  excludedRcvCents: number;
  excludedAcvCents: number;
  /** Recoverable depreciation on items not in the proposal (shown in a footnote only). */
  excludedRecDepCents: number;
  claimRcvCents: number;
  claimAcvCents: number;
  claimDepCents: number;
  /** The insurer's ACV check: claim ACV less code-upgrade ACV less the full deductible. Can be negative. */
  insuranceAcvPaymentCents: number;
  /** Insurance ACV payment less the ACV due to NMT (both net of the deductible). */
  acvDifferenceCents: number;
  upgradesTotalCents: number;
  /** Contract RCV plus upgrades: total payments to NMT (before supplements). */
  grandTotalCents: number;
  /** Customer's estimated out of pocket. Negative when insurance funds exceed the contract (display as $0). */
  outOfPocketCents: number;
}

// ---------- Build request (the rep's choices on the proposal form)

export const PRODUCT_KEYS = ["flex", "duration", "other"] as const;
export type ProductKey = (typeof PRODUCT_KEYS)[number];

export const proposalProductSchema = z.object({
  key: z.enum(PRODUCT_KEYS),
  name: z.string().max(200).optional(),
  description: z.string().max(4000).optional(),
});
export type ProposalProduct = z.infer<typeof proposalProductSchema>;

export const groupOverrideSchema = z.object({
  heading: z.string().max(200).optional(),
  summary: z.string().max(2000).optional(),
});

/** The rep's choices (Worker `build` request). Amounts in cents. */
export const proposalBuildRequestSchema = z.object({
  /** Estimate line numbers in NMT's contract. */
  included: z.array(z.number().int()).max(5000),
  /** Per group key ("<trade> - <building>"): heading/summary printed on the proposal. */
  groups: z.record(z.string(), groupOverrideSchema).optional(),
  deductibleCents: z.number().int().min(0).max(100_000_000),
  product: proposalProductSchema,
  upgrades: z
    .object({
      items: z.array(z.object({ description: z.string().max(2000), priceCents: z.number().int().optional() })).max(100).optional(),
      noCharge: z.array(z.string().max(2000)).max(100).optional(),
      totalCents: z.number().int().min(0).max(100_000_000).optional(),
    })
    .optional(),
  additionalTerms: z.array(z.string().max(2000)).max(100).optional(),
  date: z.string().max(60).optional(),
});
export type ProposalBuildRequest = z.infer<typeof proposalBuildRequestSchema> & { customer: ProposalCustomer };

/** Warning from `buildProposal`. `claim_mismatch` blocks sending (as in the Worker). */
export interface ProposalWarning {
  code: "claim_mismatch" | "deductible_mismatch";
  message: string;
}
